import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { DevboxManagerApi } from './DevboxManagerApi';
import {
  ArgoService,
  Devbox,
  DevboxEvent,
  DevboxProperty,
  DevboxStatus,
  Archetype,
  CreateDevboxRequest,
  Service,
  UpsertServiceRequest,
  ApiKey,
  ApiError,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  UpdateApiKeyRequest,
  DiagnosisResult,
} from './types';

export class DevboxManagerClient implements DevboxManagerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('proxy');
  }

  private async throwErrorFromResponse(response: Response): Promise<never> {
    let errorMessage = response.statusText;
    try {
      const body: ApiError = await response.json();
      errorMessage = body.message || errorMessage;
    } catch {
      // ignore parse errors
    }
    throw new Error(errorMessage);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      await this.throwErrorFromResponse(response);
    }
    return await response.json();
  }

  async listDevboxes(filters?: {
    name?: string;
    status?: DevboxStatus;
  }): Promise<Devbox[]> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    if (filters?.name) params.set('name', filters.name);
    if (filters?.status) params.set('status', filters.status);
    const query = params.toString();
    const url = `${baseUrl}/devbox/devboxes${query ? `?${query}` : ''}`;
    const response = await this.fetchApi.fetch(url);
    return this.handleResponse<Devbox[]>(response);
  }

  async getDevbox(name: string): Promise<Devbox> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(name)}`,
    );
    return this.handleResponse<Devbox>(response);
  }

  async createDevbox(request: CreateDevboxRequest): Promise<Devbox> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/devbox/devboxes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return this.handleResponse<Devbox>(response);
  }

  async deleteDevbox(name: string): Promise<Devbox> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    );
    return this.handleResponse<Devbox>(response);
  }

  async claimDevbox(name: string): Promise<Devbox> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(name)}/claim`,
      { method: 'POST' },
    );
    return this.handleResponse<Devbox>(response);
  }

  async claimDevboxByArchetype(archetype: Archetype): Promise<Devbox> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/claim`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archetype }),
      },
    );
    return this.handleResponse<Devbox>(response);
  }

  async listDevboxEvents(name: string): Promise<DevboxEvent[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(name)}/events`,
    );
    return this.handleResponse<DevboxEvent[]>(response);
  }

  async getDevboxProperties(name: string): Promise<DevboxProperty[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(name)}/properties`,
    );
    return this.handleResponse<DevboxProperty[]>(response);
  }

  async upsertService(
    devboxName: string,
    serviceName: string,
    request: UpsertServiceRequest,
  ): Promise<Service> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(
        devboxName,
      )}/services/${encodeURIComponent(serviceName)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    return this.handleResponse<Service>(response);
  }

  async deleteService(devboxName: string, serviceName: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(
        devboxName,
      )}/services/${encodeURIComponent(serviceName)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      await this.throwErrorFromResponse(response);
    }
  }

  async listArgoServices(): Promise<ArgoService[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('deployments');
    const response = await this.fetchApi.fetch(`${baseUrl}/service-names`);
    return this.handleResponse<ArgoService[]>(response);
  }

  async listApiKeys(): Promise<ApiKey[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/devbox/api-keys`);
    return this.handleResponse<ApiKey[]>(response);
  }

  async createApiKey(
    request: CreateApiKeyRequest,
  ): Promise<CreateApiKeyResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/devbox/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return this.handleResponse<CreateApiKeyResponse>(response);
  }

  async updateApiKey(
    id: string,
    request: UpdateApiKeyRequest,
  ): Promise<ApiKey> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/api-keys/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    return this.handleResponse<ApiKey>(response);
  }

  async deleteApiKey(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/api-keys/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      await this.throwErrorFromResponse(response);
    }
  }

  async pauseDevbox(name: string): Promise<Devbox> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(name)}/pause`,
      { method: 'POST' },
    );
    return this.handleResponse<Devbox>(response);
  }

  async resumeDevbox(name: string): Promise<Devbox> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(name)}/resume`,
      { method: 'POST' },
    );
    return this.handleResponse<Devbox>(response);
  }

  async debugDiagnosis(name: string): Promise<DiagnosisResult> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/devbox/devboxes/${encodeURIComponent(name)}/debug-diagnosis`,
      { method: 'POST' },
    );
    return this.handleResponse<DiagnosisResult>(response);
  }
}
